import { useEffect, useRef } from "react";
import { ReactComponent as CloseIcon } from "../../console/assets/icons/closeGrayIcon.svg";
import { ReactComponent as ToastNotice } from "../../console/assets/icons/toastNotice.svg";
import { useGetOrder } from "../../common/hooks/console/transaction/useGetOrder";

/**
 * 지점 포털 - 신규 발주 알림
 *
 * 본점 알림(/v1/console/alert)은 계정 구분이 없어 지점에 열 수 없다.
 * (전사 알림이 노출되고, 읽음 처리가 전역이라 본점 배지까지 지워진다)
 * 대신 이미 1계층에서 스코프된 주문을 발주 시각(branch_date) 역순으로 보여준다.
 * 종 배지와 같은 기준이라 목록과 건수가 어긋나지 않는다.
 */
const LIST_SIZE = 20;

type BranchAlarmPopProps = {
    onClose: () => void;
    onSelectOrder: (orderIdx: number) => void;
    onShowUnconfirmed: () => void;
};

const BranchAlarmPop = ({ onClose, onSelectOrder, onShowUnconfirmed }: BranchAlarmPopProps) => {
    const popRef = useRef<HTMLDivElement>(null);

    const { data, isLoading } = useGetOrder(
        1,
        LIST_SIZE,
        "all_search",
        "",
        {
            start_date: "",
            end_date: "",
            order_status: [],
            payment_status: [],
            branch_type: [],
        },
        "branch_date",
        "DESC",
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popRef.current && !popRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const list = data?.data_list ?? [];

    return (
        <div
            ref={popRef}
            className="absolute top-[35px] right-0 w-[340px] z-50 shadow-custom bg-white rounded-xl max-h-[600px] flex flex-col"
        >
            <div className="flex items-center justify-between p-3 border-b border-gray4">
                <div className="flex items-center gap-2">
                    <p className="text-gray2 font-bold text-xl">신규 발주</p>
                    {list.length > 0 && <p className="text-error font-bold">{data?.total_count?.toLocaleString()}</p>}
                </div>
                <CloseIcon className="cursor-pointer" onClick={onClose} />
            </div>

            {isLoading ? (
                <p className="text-grayicon flex justify-center items-center py-10">불러오는 중…</p>
            ) : list.length > 0 ? (
                <>
                    <div className="flex-1 overflow-y-auto">
                        {list.map(order => (
                            <button
                                key={order.order_idx}
                                className="flex gap-2 p-3 border-b border-gray4 w-full text-left hover:bg-primary4"
                                onClick={() => {
                                    onSelectOrder(order.order_idx);
                                    onClose();
                                }}
                            >
                                <ToastNotice className="min-w-6 min-h-6" />
                                <div className="flex flex-col gap-1 overflow-hidden">
                                    <p className="font-bold truncate">
                                        {order.order_number} {order.recipient_name}
                                    </p>
                                    <p className="text-gray2 text-sm truncate">
                                        {order.product_name}
                                        {order.product_quantity ? ` (${order.product_quantity}개)` : ""}
                                    </p>
                                    <p className="text-gray3 text-xs truncate">
                                        {order.delivery_date} · {order.delivery_address}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                    <button
                        className="text-primary2 text-sm underline p-3 border-t border-gray4"
                        onClick={() => {
                            onShowUnconfirmed();
                            onClose();
                        }}
                    >
                        미확인 주문만 보기
                    </button>
                </>
            ) : (
                <p className="text-grayicon flex justify-center items-center py-10">신규 발주가 없습니다.</p>
            )}
        </div>
    );
};

export default BranchAlarmPop;
